import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { Calendar, Zap } from 'lucide-react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { ALL_LOCATIONS, REGIONS } from '@/lib/locations';

const getLocationNameFromPath = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  let slug = '';

  if (parts.length > 0 && ['es', 'ru', 'zh'].includes(parts[0])) {
    // e.g. /es/sacramento
    slug = parts[1] || 'sacramento';
  } else if (parts[0] === 'locations' && parts[1] !== 'la') {
    slug = parts[1];
  } else if (parts[0] === 'locations' && parts[1] === 'la') {
    slug = parts[2];
  } else {
    slug = parts[0] || 'sacramento';
  }

  const found = ALL_LOCATIONS.find(l => l.slug === slug);
  if (found) return found.name;
  
  const region = REGIONS[slug as keyof typeof REGIONS];
  if (region) return region.name;

  return "Sacramento";
};

export const FloatingDesktopCTA = () => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const routerLocation = useRouterLocation();
  const locationName = getLocationNameFromPath(routerLocation.pathname);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past the hero section
      if (window.scrollY > 600) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isMobile || !isVisible) return null;

  const isEs = routerLocation.pathname.startsWith('/es');
  const isRu = routerLocation.pathname.startsWith('/ru');
  const isZh = routerLocation.pathname.startsWith('/zh');
  const isReturning = typeof window !== 'undefined' ? (parseInt(localStorage.getItem("site_visits") || "0") > 1) : false;

  const textReady = isZh ? "准备好迎接洁净的家了吗？" : isRu ? "Готовы к чистому дому?" : isEs ? "¿Listo para un hogar impecable?" : "Ready for a spotless home?";
  const textRated = isZh ? `${locationName} 排名第一的服务` : isRu ? `Лучший сервис в ${locationName}` : isEs ? `El servicio #1 en ${locationName}` : `${locationName}'s Professional Cleaning Service`;
  const textPrice = isZh ? (isReturning ? "再次预约" : "获取报价") : isRu ? (isReturning ? "Записаться снова" : "Узнать цену") : isEs ? (isReturning ? "Reservar de nuevo" : "Ver Precio") : (isReturning ? "Book Again" : "Instant Price");
  const textBook = isZh ? (isReturning ? "再次预约" : "立即预约") : isRu ? (isReturning ? "Записаться снова" : "Записаться") : isEs ? (isReturning ? "Reservar de nuevo" : "Reservar") : (isReturning ? "Book Again" : "Book Now");

  return (
    <div className="fixed top-[80px] left-0 right-0 z-40 animate-in fade-in slide-in-from-top-4 duration-500">
      <div className="bg-background/95 backdrop-blur-md border-b border-border shadow-md px-4 py-3 flex items-center justify-center gap-10 whitespace-nowrap">
        <div className="hidden lg:flex flex-col">
          <span className="text-sm font-bold text-foreground">{textReady}</span>
          <span className="text-[10px] uppercase tracking-wider text-primary font-bold">{textRated}</span>
        </div>
        
        <div className="flex items-center gap-3">
          <a 
            href="/book?view=custom-plan"
            aria-label={textPrice}
            className="transition-transform hover:scale-105 active:scale-95"
          >
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-full font-bold border-primary text-primary hover:bg-primary/10 hover:text-primary px-6"
            >
              <Zap className="h-4 w-4 mr-2 fill-primary/20" />
              {textPrice}
            </Button>
          </a>
          
          <a 
            href="/book?view=custom-plan"
            aria-label={textBook}
            className="transition-transform hover:scale-105 active:scale-95"
          >
            <Button 
              size="sm" 
              className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary-hover px-8 shadow-lg shadow-primary/20"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {textBook}
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};
